import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';

// Import des sous-composants
import CandidatBulkActions from './candidat/CandidatBulkActions';
import CandidatFilters from './candidat/CandidatFilters';
import CandidatsList from './candidat/CandidatsList';
import CandidatForm from './candidat/CandidatForm';
import CandidatDetails from './candidat/CandidatDetails';

const CandidatsTab = () => {
  // États pour les données
  const [candidats, setCandidats] = useState([]);
  const [sessions, setSessions] = useState([]);
  
  // États pour le chargement
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // États pour la recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedStatut, setSelectedStatut] = useState('');
  
  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  const [candidatToDelete, setCandidatToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});
  
  // États pour le formulaire
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    sexe: 'M',
    nationalite: 'Sénégalaise',
    telephone: '',
    email: '',
    adresse: '',
    session_concours: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  
  // États pour la sélection multiple
  const [selectedCandidats, setSelectedCandidats] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const statutOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'BROUILLON', label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
    { value: 'SOUMIS', label: 'Soumis', color: 'bg-blue-100 text-blue-800' },
    { value: 'CONFIRME', label: 'Confirmé', color: 'bg-green-100 text-green-800' },
    { value: 'REJETE', label: 'Rejeté', color: 'bg-red-100 text-red-800' },
    { value: 'ANNULE', label: 'Annulé', color: 'bg-orange-100 text-orange-800' }
  ];

  useEffect(() => {
    fetchData();
    fetchSessions();
  }, [currentPage, selectedSession, selectedStatut, searchTerm]);

  const fetchSessions = async () => {
    try {
      const response = await api.get('/concours/sessions/');
      if (response.data) {
        const sessionData = response.data.data || response.data.results || response.data || [];
        setSessions(Array.isArray(sessionData) ? sessionData : []);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
      setSessions([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      
      if (selectedSession) params.append('session', selectedSession);
      if (selectedStatut) params.append('statut', selectedStatut);
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get(`/concours/candidats/?${params}`);
      setCandidats(response.data.data || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Erreur lors du chargement des candidats:', error);
      toast.error('Erreur lors du chargement des candidats');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des candidats pour la recherche
  const filteredCandidats = useMemo(() => {
    return candidats.filter(candidat => {
      const searchTermLower = searchTerm.toLowerCase();
      
      return candidat.nom_complet?.toLowerCase().includes(searchTermLower) ||
        candidat.numero_candidat?.toLowerCase().includes(searchTermLower) ||
        candidat.telephone?.toLowerCase().includes(searchTermLower) ||
        candidat.email?.toLowerCase().includes(searchTermLower);
    });
  }, [candidats, searchTerm]);

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal
  const handleOpenModal = (candidat = null) => {
    if (candidat) {
      setSelectedCandidat(candidat);
      setFormData({
        nom: candidat.nom || '',
        prenom: candidat.prenom || '',
        date_naissance: candidat.date_naissance || '',
        lieu_naissance: candidat.lieu_naissance || '',
        sexe: candidat.sexe || 'M',
        nationalite: candidat.nationalite || 'Sénégalaise',
        telephone: candidat.telephone || '',
        email: candidat.email || '',
        adresse: candidat.adresse || '',
        session_concours: candidat.session_concours?.id || ''
      });
    } else {
      setSelectedCandidat(null);
      setFormData({
        nom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        sexe: 'M',
        nationalite: 'Sénégalaise',
        telephone: '',
        email: '',
        adresse: '',
        session_concours: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    if (!formData.nom.trim()) {
      errors.nom = "Le nom est requis";
    }
    if (!formData.prenom.trim()) {
      errors.prenom = "Le prénom est requis";
    }
    if (!formData.date_naissance) {
      errors.date_naissance = "La date de naissance est requise";
    }
    if (!formData.lieu_naissance.trim()) {
      errors.lieu_naissance = "Le lieu de naissance est requis";
    }
    if (!formData.telephone.trim()) {
      errors.telephone = "Le téléphone est requis";
    }
    if (!formData.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "L'email est invalide";
    }
    if (!formData.adresse.trim()) {
      errors.adresse = "L'adresse est requise";
    }
    if (!formData.session_concours) {
      errors.session_concours = "La session de concours est requise";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let response;
      if (selectedCandidat) {
        // Mise à jour
        response = await api.put(`/concours/candidats/${selectedCandidat.id}/`, formData);
        toast.success("Candidat mis à jour avec succès");
      } else {
        // Création
        response = await api.post('/concours/candidats/', formData);
        toast.success("Candidat créé avec succès");
      }

      fetchData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour changer le statut d'un candidat
  const handleChangeStatut = async (candidatId, nouveauStatut) => {
    console.log('🔄 Changement de statut:', { candidatId, nouveauStatut });
    
    try {
      const response = await api.patch(`/concours/candidats/${candidatId}/`, { 
        statut_inscription: nouveauStatut 
      });
      
      console.log('✅ Réponse API:', response.data);
      
      const statutLabel = statutOptions.find(s => s.value === nouveauStatut)?.label || nouveauStatut;
      toast.success(`Candidat ${statutLabel.toLowerCase()} avec succès`);
      
      // Forcer le rechargement des données
      await fetchData();
    } catch (error) {
      console.error('❌ Erreur lors du changement de statut:', error);
      console.error('❌ Détails de l\'erreur:', error.response?.data);
      toast.error('Erreur lors du changement de statut');
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (candidat) => {
    setCandidatToDelete(candidat);
    
    setConfirmConfig({
      title: "Supprimer le candidat",
      message: `Êtes-vous sûr de vouloir supprimer le candidat ${candidat.nom_complet} ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!candidatToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/candidats/${candidatToDelete.id}/`);
      toast.success("Candidat supprimé avec succès");
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error("Impossible de supprimer ce candidat. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Ouvrir le modal de détails
  const handleOpenDetailsModal = (candidat) => {
    setSelectedCandidat(candidat);
    setIsDetailsModalOpen(true);
  };

  // Fonction pour confirmer les candidats en lot
  const handleBulkConfirm = async () => {
    if (selectedCandidats.length === 0) {
      toast.error('Veuillez sélectionner au moins un candidat');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir confirmer ${selectedCandidats.length} candidat(s) ?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const promises = selectedCandidats.map(candidatId => 
        api.patch(`/concours/candidats/${candidatId}/`, { statut_inscription: 'CONFIRME' })
      );
      
      await Promise.all(promises);
      toast.success(`${selectedCandidats.length} candidat(s) confirmé(s) avec succès`);
      setSelectedCandidats([]);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la confirmation en lot:', error);
      toast.error('Erreur lors de la confirmation des candidats');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Fonction pour rejeter les candidats en lot
  const handleBulkReject = async () => {
    if (selectedCandidats.length === 0) {
      toast.error('Veuillez sélectionner au moins un candidat');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir rejeter ${selectedCandidats.length} candidat(s) ?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const promises = selectedCandidats.map(candidatId => 
        api.patch(`/concours/candidats/${candidatId}/`, { statut_inscription: 'REJETE' })
      );
      
      await Promise.all(promises);
      toast.success(`${selectedCandidats.length} candidat(s) rejeté(s) avec succès`);
      setSelectedCandidats([]);
      fetchData();
    } catch (error) {
      console.error('Erreur lors du rejet en lot:', error);
      toast.error('Erreur lors du rejet des candidats');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Fonction pour sélectionner/désélectionner un candidat
  const handleSelectCandidat = (candidatId) => {
    setSelectedCandidats(prev => 
      prev.includes(candidatId) 
        ? prev.filter(id => id !== candidatId)
        : [...prev, candidatId]
    );
  };

  // Fonction pour sélectionner/désélectionner tous les candidats
  const handleSelectAll = () => {
    if (selectedCandidats.length === filteredCandidats.length) {
      setSelectedCandidats([]);
    } else {
      setSelectedCandidats(filteredCandidats.map(c => c.id));
    }
  };

  const getStatusColor = (statut) => {
    const statusOption = statutOptions.find(opt => opt.value === statut);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (statut) => {
    const statusOption = statutOptions.find(opt => opt.value === statut);
    return statusOption ? statusOption.label : statut;
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec actions en lot */}
      <CandidatBulkActions
        selectedCandidats={selectedCandidats}
        bulkActionLoading={bulkActionLoading}
        onBulkConfirm={handleBulkConfirm}
        onBulkReject={handleBulkReject}
        onOpenModal={handleOpenModal}
      />

      {/* Filtres */}
      <CandidatFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedSession={selectedSession}
        setSelectedSession={setSelectedSession}
        selectedStatut={selectedStatut}
        setSelectedStatut={setSelectedStatut}
        sessions={sessions}
        statutOptions={statutOptions}
        totalCandidats={filteredCandidats.length}
      />

      {/* Liste des candidats */}
      <CandidatsList
        candidats={filteredCandidats}
        loading={loading}
        selectedCandidats={selectedCandidats}
        onSelectCandidat={handleSelectCandidat}
        onSelectAll={handleSelectAll}
        onChangeStatut={handleChangeStatut}
        onOpenDetailsModal={handleOpenDetailsModal}
        onOpenModal={handleOpenModal}
        onOpenDeleteModal={handleOpenDeleteModal}
        getStatusColor={getStatusColor}
        getStatusLabel={getStatusLabel}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedCandidat ? 'Modifier le candidat' : 'Nouveau candidat'}
        maxWidth="max-w-4xl"
      >
        <CandidatForm
          formData={formData}
          formErrors={formErrors}
          sessions={sessions}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          selectedCandidat={selectedCandidat}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title="Détails du Candidat"
        maxWidth="max-w-4xl"
      >
        <CandidatDetails
          candidat={selectedCandidat}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
        />
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le candidat"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce candidat ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default CandidatsTab; 